import { BiCardDetailView } from '@euler/app/flows/bi-dashboards/card/BiCardDetailView';
import { BiDashboardView } from '@euler/app/flows/bi-dashboards/dashboard/BiDashboardView';
import { BiDateChoice } from '@euler/app/flows/bi-dashboards/dashboard/functions/biDateRange';
import { BiDashboardListView } from '@euler/app/flows/bi-dashboards/list/BiDashboardListView';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import {
  ServiceFactoryProvider,
  useServiceFactory,
} from '@euler/services/factory';
import { useMemo, useState } from 'react';
import { FixtureBiDashboardService } from './FixtureBiDashboardService';
import { FixtureStoreService } from './FixtureStoreService';

export interface BiDashboardsPlaygroundParams {
  /** Opens this fixture dashboard first, e.g. `bidash_custom000…`. */
  dashboardId?: string;
  /** Then this card's page. */
  cardId?: string;
  /** The first write is refused as stale, to see the reload. */
  conflict?: boolean;
  /** The editor's first catalog read fails, to see its retry. */
  catalogsFail?: boolean;
  /** The caller may only view, to see the read-only pages. */
  viewer?: boolean;
}

/** The dashboard list and pages over in-memory fixtures. */
export const BiDashboardsPlayground = ({
  dashboardId,
  cardId,
  conflict,
  catalogsFail,
  viewer,
}: BiDashboardsPlaygroundParams) => {
  const services = useServiceFactory();
  const mobile = useIsMobileLayout();
  const fixtures = useMemo(
    () => ({
      ...services,
      biService: new FixtureBiDashboardService(
        !!conflict,
        !!catalogsFail,
        !!viewer,
      ),
      storeService: new FixtureStoreService(),
    }),
    [services, conflict, catalogsFail, viewer],
  );
  const [open, setOpen] = useState<string | null>(dashboardId ?? null);
  const [card, setCard] = useState<string | null>(cardId ?? null);
  const [choice, setChoice] = useState<BiDateChoice>({ kind: 'saved' });
  return (
    <ServiceFactoryProvider serviceFactory={fixtures}>
      {open && card ? (
        <BiCardDetailView
          key={card}
          dashboardId={open}
          cardId={card}
          choice={choice}
          onChoice={setChoice}
          onBack={() => setCard(null)}
        />
      ) : open ? (
        <BiDashboardView
          key={open}
          dashboardId={open}
          choice={choice}
          onChoice={setChoice}
          onBack={() => setOpen(null)}
          onOpenCard={setCard}
          onSwitch={setOpen}
          onManage={() => setOpen(null)}
          onOpenExports={() => undefined}
        />
      ) : (
        <BiDashboardListView
          mobile={mobile}
          onBack={() => undefined}
          onOpen={setOpen}
          onAskAi={() => undefined}
          onOpenLibrary={() => undefined}
          onOpenExports={() => undefined}
        />
      )}
    </ServiceFactoryProvider>
  );
};

export const BiDashboardsPlaygroundPage = wrapNavigatorScreen(
  BiDashboardsPlayground,
  { title: 'BI Dashboards', headerShown: false, headerMode: 'float' },
);
